"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createCmsHptHandler = createCmsHptHandler;
async function fetchCmsHptContent(apiKey, entryName) {
    const query = encodeURIComponent(JSON.stringify({ name: entryName }));
    const url = `https://cdn.builder.io/api/v3/content/site-properties` +
        `?apiKey=${encodeURIComponent(apiKey)}` +
        `&query=${query}` +
        `&limit=1` +
        `&fields=data.cmsHptContent`;
    const response = await fetch(url);
    if (!response.ok) {
        return null;
    }
    const body = (await response.json());
    const content = body.results?.[0]?.data?.cmsHptContent;
    if (typeof content !== "string" || !content.trim()) {
        return null;
    }
    return content;
}
/**
 * Creates a Next.js API handler for CMS hospital price transparency (cms-hpt.txt).
 *
 * Content is loaded from the `cmsHptContent` longText field on the site-properties
 * entry for the current app's Builder.io space.
 *
 * Resilience: the last successfully fetched content is retained in memory per
 * cold-start instance. If a later Builder.io fetch fails or returns empty, the
 * handler serves that last-known-good copy instead of erroring — important for a
 * federal price-transparency file that previously shipped as a static asset.
 */
function createCmsHptHandler(config = {}) {
    const { builderApiKeyEnvVar = "NEXT_PUBLIC_BUILDER_API_KEY", sitePropertiesEntryEnvVar = "NEXT_PUBLIC_SITE_PROPERTIES_ENTRY", cacheMaxAgeSeconds = 300, } = config;
    // Last content successfully fetched from Builder.io, kept for this instance's
    // lifetime so transient CDN failures don't take the compliance file offline.
    let lastKnownGoodContent = null;
    return async function handler(_req, res) {
        const apiKey = process.env[builderApiKeyEnvVar];
        const entryName = process.env[sitePropertiesEntryEnvVar] || "orlando-health";
        if (!apiKey) {
            res.status(503).send("CMS API key is not configured");
            return;
        }
        const sendContent = (content) => {
            res.setHeader("Content-Type", "text/plain; charset=utf-8");
            res.setHeader("Cache-Control", `s-maxage=${cacheMaxAgeSeconds}, stale-while-revalidate=${cacheMaxAgeSeconds}`);
            res.status(200).send(content);
        };
        try {
            const content = await fetchCmsHptContent(apiKey, entryName);
            if (content) {
                lastKnownGoodContent = content;
                sendContent(content);
                return;
            }
            // Empty/unconfigured response: fall back to last-known-good if we have one.
            if (lastKnownGoodContent) {
                sendContent(lastKnownGoodContent);
                return;
            }
            res.status(404).send("cms-hpt.txt is not configured");
        }
        catch {
            // Transient fetch failure: serve last-known-good rather than 502 if available.
            if (lastKnownGoodContent) {
                sendContent(lastKnownGoodContent);
                return;
            }
            res.status(502).send("Failed to load cms-hpt.txt");
        }
    };
}
